import { useState } from "react";

import { Box, Button, HStack, Text, useDisclosure } from "@chakra-ui/react";

import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { BaseFormProps } from "@/jenesys-types/forms";
import { BankStatementUpload } from "@/jenesys-types/jenesys-entities/config";

import { PreviewFile } from "@/components/features/transactions/TranactionPreviewFile";
import { Form } from "@/components/forms/Form/Form";
import useNamedNavigate from "@/hooks/navigation/useNamedNavigate";
import {
  selectReconciliation,
  syncStatement
} from "@/store/features/reconciliation/reconciliation";
import { toastSuccess } from "@/util/helpers";
import { StatementUploadEditFormSchema } from "@/validation/schema/reconciliations/statementUploadEditFormSchema";

import { ConfirmConvertStatementModal } from "./ConfirmConvertStatementModal";
import { StatementUploadEditTable } from "./StatementUploadEditTable";

const initialLine = {
  Date: "",
  Description: "",
  Amount: "",
  Supplier: ""
};

const defaultInitialValues = {
  extracted_details: [initialLine]
};

export interface ReconStatementUploadEditFormProps
  extends BaseFormProps<BankStatementUpload> {}

export const ReconStatementUploadEditForm: React.FC<
  ReconStatementUploadEditFormProps & { onSave: (data: any) => void }
> = ({
  initialValues = defaultInitialValues as BankStatementUpload,
  isSaving,
  onSave
}) => {
  const { status } = initialValues;
  const [showPreview, setShowPreview] = useState(false);
  const confirmModalDisclosure = useDisclosure();
  const { trigger, getValues } = useFormContext<BankStatementUpload>();
  const dispatch = useDispatch();
  const { clientId, reconciliationId } = useParams();
  const namedNavigate = useNamedNavigate();
  const { syncStatementLoading } = useSelector(selectReconciliation);
  const isConfirmed = status === "CONFIRMED";

  const handleContinue = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }
    confirmModalDisclosure.onOpen();
  };

  const handleSave = () => {
    const values = getValues();
    onSave({ extracted_details: values.extracted_details });
  };

  const handleConfirm = () => {
    dispatch(
      syncStatement({
        bankAccountId: initialValues.bank_account_id!,
        documentId: initialValues.id!,
        reconciliationId: reconciliationId!,
        onSuccess: () => {
          toastSuccess(
            "The transactions lines are now ready for reconciliation."
          );
          confirmModalDisclosure.onClose();
          namedNavigate("bankStatement.list", {
            clientId,
            reconciliationId
          });
        }
      })
    );
  };

  return (
    <Box px={16} pb={20} mt={-8}>
      <ConfirmConvertStatementModal
        {...confirmModalDisclosure}
        onConfirm={handleConfirm}
        isLoading={syncStatementLoading}
      />
      <Text fontSize={14}>
        Review the extracted data from your uploaded statement. You can edit any
        field, remove rows, or add new lines if needed.
      </Text>

      <HStack align="stretch" spacing={4} mt={10}>
        {showPreview && (
          <Box bg="white" w="full" flexShrink={0} maxW="40%">
            {initialValues.pdf_url && (
              <PreviewFile
                frameHeight={700}
                imageUrl={initialValues.pdf_url}
                imageDownloadUrl={initialValues.pdf_download_url!}
              />
            )}
          </Box>
        )}
        <Box w="full" bg="white" px={8} pt={2.5} pb={8} rounded={16}>
          <Box w="full">
            <HStack justify="space-between" align="flex-end">
              <Box bg="blackAlpha.50" px={5} py={2} rounded={8}>
                <Text fontSize={14} fontWeight={500}>
                  Money out (Statement Total)
                </Text>
                <Text fontSize={16} fontWeight={700}>
                  {initialValues.total_amount?.toFixed(2) || 0}
                </Text>
              </Box>

              <HStack>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? "Close View" : "See Bank Statement"}
                </Button>
                <Button
                  isDisabled={isConfirmed}
                  variant="outline"
                  size="sm"
                  loadingText="Saving..."
                  isLoading={isSaving}
                  onClick={handleSave}
                >
                  Save
                </Button>
                <Button
                  isDisabled={isConfirmed}
                  size="sm"
                  onClick={handleContinue}
                >
                  Continue
                </Button>
              </HStack>
            </HStack>
          </Box>

          <StatementUploadEditTable />
        </Box>
      </HStack>
    </Box>
  );
};

export const ReconStatementUploadEditFormWrapper: React.FC<
  ReconStatementUploadEditFormProps
> = (props) => {
  const {
    onSave,
    initialValues = defaultInitialValues as BankStatementUpload
  } = props;

  return (
    <Form
      onSubmit={(item) => onSave(item.extracted_details)}
      initialValues={initialValues}
      validationSchema={StatementUploadEditFormSchema}
    >
      <ReconStatementUploadEditForm {...props} />
    </Form>
  );
};
